import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button, Card, Modal, Pill, TextField, TextareaField, fmtBRL } from "@atlas/ui/web";
import type { AssuntoAlteracaoConsignacao, ConsignacaoFacultativaDoServidor } from "@atlas/sdk";
import { atlas } from "../../lib/sdk";
import { mensagemDeErro } from "../../lib/erro-validacao";
import { ImpersonateReadOnlyNotice, useIsImpersonating } from "../ImpersonateBar";

// OUTROS DESCONTOS EM FOLHA (#276): plano de saude, odonto, seguro, mensalidade.
//
// O servidor ve o produto real ("Plano de saude — Unimed, via ASSALCE") e pode
// pedir o cancelamento ou a alteracao. O pedido NAO muda o desconto: quem
// encerra ou altera e' a consignataria, que responde no portal dela (#318). A
// tela diz isso antes do envio, para ninguem sair achando que o desconto parou.
//
// `consignacoes`:
//   undefined = cache antigo, de antes da #276 — nada a mostrar;
//   null      = a API nao conseguiu ler — dizer, e nao fingir lista vazia;
//   []        = o servidor nao tem nenhuma — a secao nao aparece.

const fmtData = (iso: string) => new Date(iso).toLocaleDateString("pt-BR");
const fmtCompetencia = (c: string) => (/^\d{6}$/.test(c) ? `${c.slice(4)}/${c.slice(0, 4)}` : c);

/** "Unimed, via ASSALCE"; sem credor final, so' a consignataria. */
export function quemPresta(c: Pick<ConsignacaoFacultativaDoServidor, "consignatariaNome" | "credorFinalNome">): string | null {
  if (c.credorFinalNome && c.consignatariaNome) return `${c.credorFinalNome}, via ${c.consignatariaNome}`;
  return c.credorFinalNome ?? c.consignatariaNome;
}

export const ROTULO_ASSUNTO: Record<AssuntoAlteracaoConsignacao, string> = {
  mudar_plano: "Mudar de plano",
  dependentes: "Incluir ou excluir dependente",
  outro: "Outro assunto",
};

export function OutrosDescontos({
  consignacoes,
  onMudou,
  titulo = "Outros descontos em folha",
  subtitulo = "Planos, seguros e mensalidades descontados no seu contracheque.",
}: {
  consignacoes: ConsignacaoFacultativaDoServidor[] | null | undefined;
  onMudou: () => void | Promise<unknown>;
  titulo?: string;
  subtitulo?: string;
}) {
  const [pedindo, setPedindo] = useState<ConsignacaoFacultativaDoServidor | null>(null);
  const [alterando, setAlterando] = useState<ConsignacaoFacultativaDoServidor | null>(null);
  // #282: a inclusao (ou o aumento) que espera a autorizacao do servidor.
  const [autorizando, setAutorizando] = useState<ConsignacaoFacultativaDoServidor | null>(null);
  const impersonando = useIsImpersonating();

  if (consignacoes === undefined) return null;
  if (consignacoes !== null && consignacoes.length === 0) return null;

  return (
    <section style={{ marginTop: 32 }} aria-labelledby="outros-descontos-titulo">
      <h2 id="outros-descontos-titulo" style={{ fontSize: "1.05rem", margin: "0 0 4px" }}>
        {titulo}
      </h2>
      <p style={{ margin: "0 0 12px", fontSize: 13, color: "var(--text-muted)" }}>
        {subtitulo}
      </p>
      {consignacoes === null ? (
        <Card>
          <p role="status" style={{ margin: 0, fontSize: 13, color: "var(--text-muted)" }}>
            Não foi possível carregar os outros descontos agora. Tente de novo em instantes.
          </p>
        </Card>
      ) : (
        <div style={{ display: "grid", gap: 12 }}>
          <ImpersonateReadOnlyNotice acao="pedir cancelamento ou alteração" />
          {consignacoes.map((c) => {
            const prestador = quemPresta(c);
            return (
              <Card key={c.id}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                  <div>
                    <div style={{ fontWeight: 700 }}>{c.rotulo}</div>
                    {prestador ? (
                      <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 2 }}>{prestador}</div>
                    ) : null}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {c.situacao === "aguardando_autorizacao" ? (
                      <Pill variant="pendente">aguardando sua autorização</Pill>
                    ) : c.autorizacao ? (
                      <Pill variant="pendente">aumento aguardando autorização</Pill>
                    ) : c.solicitacaoAberta ? (
                      <Pill variant="pendente">
                        {c.solicitacaoAberta.tipo === "alteracao" ? "alteração pedida" : "cancelamento pedido"}
                      </Pill>
                    ) : c.situacao === "encerrando" ? (
                      <Pill variant="expirado">encerrando</Pill>
                    ) : (
                      <Pill variant="aceita">ativo</Pill>
                    )}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 24, flexWrap: "wrap", marginTop: 12, fontSize: 13 }}>
                  <div>
                    <div style={{ color: "var(--text-muted)", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em" }}>Valor mensal</div>
                    <div style={{ fontWeight: 700, color: "var(--accent)" }}>{fmtBRL(c.valorCentavos / 100)}</div>
                  </div>
                  <div>
                    <div style={{ color: "var(--text-muted)", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em" }}>Desde</div>
                    <div>{fmtCompetencia(c.competenciaInicio)}</div>
                  </div>
                  {c.competenciaFim ? (
                    <div>
                      <div style={{ color: "var(--text-muted)", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em" }}>Último desconto</div>
                      <div>{fmtCompetencia(c.competenciaFim)}</div>
                    </div>
                  ) : null}
                </div>
                <div style={{ marginTop: 14 }}>
                  {c.autorizacao ? (
                    <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                      <span style={{ fontSize: 13, color: "var(--text-muted)" }}>
                        {c.autorizacao.tipo === "inclusao" ? "Este desconto só começa" : `O novo valor de ${fmtBRL(c.autorizacao.valorCentavos / 100)} só vale`}{" "}
                        depois que você autorizar — até {fmtData(`${c.autorizacao.prazoAte}T12:00:00`)}.
                      </span>
                      {!impersonando ? (
                        <Button size="sm" onClick={() => setAutorizando(c)}>
                          Autorizar
                        </Button>
                      ) : null}
                    </div>
                  ) : c.solicitacaoAberta ? (
                    <span style={{ fontSize: 13, color: "var(--text-muted)" }}>
                      {c.solicitacaoAberta.tipo === "alteracao"
                        ? `Alteração solicitada${c.solicitacaoAberta.assunto ? ` (${ROTULO_ASSUNTO[c.solicitacaoAberta.assunto].toLowerCase()})` : ""}`
                        : "Cancelamento solicitado"}{" "}
                      em {fmtData(c.solicitacaoAberta.solicitadaEm)} — a consignatária responde até{" "}
                      {fmtData(c.solicitacaoAberta.responderAte)}.
                    </span>
                  ) : !impersonando && (c.podeSolicitarAlteracao || c.podeSolicitarCancelamento) ? (
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {c.podeSolicitarAlteracao ? (
                        <Button size="sm" variant="ghost" onClick={() => setAlterando(c)}>
                          Pedir alteração
                        </Button>
                      ) : null}
                      {c.podeSolicitarCancelamento ? (
                        <Button size="sm" variant="ghost" onClick={() => setPedindo(c)}>
                          Pedir cancelamento
                        </Button>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </Card>
            );
          })}
        </div>
      )}
      {autorizando?.autorizacao ? (
        <AutorizarModal
          consignacao={autorizando}
          onClose={() => setAutorizando(null)}
          onAutorizado={async () => {
            setAutorizando(null);
            await onMudou();
          }}
        />
      ) : null}
      {alterando ? (
        <PedirAlteracaoModal
          consignacao={alterando}
          onClose={() => setAlterando(null)}
          onEnviado={async () => {
            setAlterando(null);
            await onMudou();
          }}
        />
      ) : null}
      {pedindo ? (
        <PedirCancelamentoModal
          consignacao={pedindo}
          onClose={() => setPedindo(null)}
          onEnviado={async () => {
            setPedindo(null);
            await onMudou();
          }}
        />
      ) : null}
    </section>
  );
}

function PedirCancelamentoModal({
  consignacao,
  onClose,
  onEnviado,
}: {
  consignacao: ConsignacaoFacultativaDoServidor;
  onClose: () => void;
  onEnviado: () => void | Promise<void>;
}) {
  const [motivo, setMotivo] = useState("");
  const [erro, setErro] = useState<unknown>(null);
  const enviar = useMutation({
    mutationFn: () => atlas.servidor.solicitarCancelamentoConsignacao(consignacao.id, motivo.trim() || undefined),
    onSuccess: onEnviado,
    onError: setErro,
  });
  const prestador = quemPresta(consignacao);
  return (
    <Modal
      open
      onClose={onClose}
      title="Pedir cancelamento"
      subtitle={prestador ? `${consignacao.rotulo} — ${prestador}` : consignacao.rotulo}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={enviar.isPending}>
            Voltar
          </Button>
          <Button onClick={() => enviar.mutate()} disabled={enviar.isPending}>
            {enviar.isPending ? "Enviando…" : "Enviar pedido"}
          </Button>
        </>
      }
    >
      <div style={{ display: "grid", gap: 12 }}>
        <p style={{ margin: 0, fontSize: 14, lineHeight: 1.5 }}>
          O desconto de <strong>{fmtBRL(consignacao.valorCentavos / 100)}</strong> continua até a consignatária
          encerrar. Seu pedido vai para a consignatária, e a resposta aparece em Meus benefícios e no seu e-mail.
        </p>
        <TextareaField
          label="Motivo (opcional)"
          value={motivo}
          maxLength={500}
          rows={3}
          onChange={(e) => setMotivo(e.target.value)}
        />
        {erro ? (
          <div role="alert" style={{ fontSize: 13, color: "var(--danger, #DC2626)" }}>
            {mensagemDeErro(erro, "Não foi possível enviar o pedido.")}
          </div>
        ) : null}
      </div>
    </Modal>
  );
}

const ASSUNTOS = Object.keys(ROTULO_ASSUNTO) as AssuntoAlteracaoConsignacao[];
const DESCRICAO_MIN = 10;

/** O pedido de alteracao: o assunto e o que mudar. O valor novo, se houver, quem diz e' a consignataria. */
function PedirAlteracaoModal({
  consignacao,
  onClose,
  onEnviado,
}: {
  consignacao: ConsignacaoFacultativaDoServidor;
  onClose: () => void;
  onEnviado: () => void | Promise<void>;
}) {
  const [assunto, setAssunto] = useState<AssuntoAlteracaoConsignacao>("mudar_plano");
  const [descricao, setDescricao] = useState("");
  const [erro, setErro] = useState<unknown>(null);
  const enviar = useMutation({
    mutationFn: () => atlas.servidor.solicitarAlteracaoConsignacao(consignacao.id, { assunto, descricao: descricao.trim() }),
    onSuccess: onEnviado,
    onError: setErro,
  });
  const prestador = quemPresta(consignacao);
  const curta = descricao.trim().length < DESCRICAO_MIN;
  return (
    <Modal
      open
      onClose={onClose}
      title="Pedir alteração"
      subtitle={prestador ? `${consignacao.rotulo} — ${prestador}` : consignacao.rotulo}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={enviar.isPending}>
            Voltar
          </Button>
          <Button onClick={() => enviar.mutate()} disabled={enviar.isPending || curta}>
            {enviar.isPending ? "Enviando…" : "Enviar pedido"}
          </Button>
        </>
      }
    >
      <div style={{ display: "grid", gap: 12 }}>
        <div role="radiogroup" aria-label="O que você quer mudar" style={{ display: "grid", gap: 8 }}>
          {ASSUNTOS.map((a) => (
            <label key={a} style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 14, cursor: "pointer" }}>
              <input type="radio" name="assunto" checked={assunto === a} onChange={() => setAssunto(a)} />
              {ROTULO_ASSUNTO[a]}
            </label>
          ))}
        </div>
        <TextareaField
          label="Descreva o que você quer mudar"
          value={descricao}
          maxLength={1000}
          rows={4}
          placeholder={
            assunto === "dependentes"
              ? "Ex.: incluir minha filha Ana, nascida em 2020."
              : assunto === "mudar_plano"
                ? "Ex.: passar do plano apartamento para o enfermaria."
                : "Conte o que você precisa."
          }
          onChange={(e) => setDescricao(e.target.value)}
          hint={curta ? `Pelo menos ${DESCRICAO_MIN} caracteres.` : undefined}
        />
        <p style={{ margin: 0, fontSize: 13, color: "var(--text-muted)", lineHeight: 1.5 }}>
          O desconto de <strong>{fmtBRL(consignacao.valorCentavos / 100)}</strong> continua como está até a
          consignatária responder. Se a mudança alterar o valor, ela informa o novo valor na resposta.
        </p>
        {erro ? (
          <div role="alert" style={{ fontSize: 13, color: "var(--danger, #DC2626)" }}>
            {mensagemDeErro(erro, "Não foi possível enviar o pedido.")}
          </div>
        ) : null}
      </div>
    </Modal>
  );
}

/**
 * A AUTORIZACAO (#282): o servidor le o que autoriza, marca o aceite e confirma
 * pela tela (com a senha de transacao, quando o orgao a exige) ou pelo codigo
 * enviado ao e-mail. O canal oferecido e' o que o orgao aceita.
 */
export function AutorizarModal({
  consignacao,
  onClose,
  onAutorizado,
}: {
  consignacao: ConsignacaoFacultativaDoServidor;
  onClose: () => void;
  onAutorizado: () => void | Promise<unknown>;
}) {
  const a = consignacao.autorizacao!;
  const [canal, setCanal] = useState<"web" | "token">(a.canal === "token" ? "token" : "web");
  const [aceite, setAceite] = useState(false);
  const [senha, setSenha] = useState("");
  const [codigo, setCodigo] = useState("");
  const [envio, setEnvio] = useState<string | null>(null);
  const pedirCodigo = useMutation({
    mutationFn: () => atlas.servidor.pedirCodigoAutorizacao(consignacao.id),
    onSuccess: (r) =>
      setEnvio(r.enviado ? `Enviamos o código para ${r.destino}.` : r.aviso ?? "Não conseguimos enviar o código agora."),
  });
  // O TERMO VEM DA API, nao e' montado aqui: o que o servidor le e' o que o
  // hash prova (revisao de seguranca do PR #311). Se a pendencia mudar no meio,
  // o autorizar volta 409 e o termo e' buscado de novo.
  const termo = useQuery({
    queryKey: ["servidor", "autorizacao-termo", consignacao.id],
    queryFn: () => atlas.servidor.termoDeAutorizacao(consignacao.id),
    staleTime: 0,
  });
  // O ACEITE E' DO TEXTO QUE ESTA' NA TELA: termo novo (outro hash), aceite
  // desmarcado — senao, depois de um 409, um segundo clique autorizava o valor
  // novo com o aceite do velho (revalidacao do PR #311).
  const hashDoTermo = termo.data?.hashDoTermo;
  useEffect(() => {
    setAceite(false);
  }, [hashDoTermo]);
  const autorizar = useMutation({
    mutationFn: () =>
      atlas.servidor.autorizarConsignacao(consignacao.id, {
        canal,
        aceite: true,
        hashDoTermo: termo.data!.hashDoTermo,
        ...(canal === "token" ? { codigo } : { senha }),
      }),
    onSuccess: () => void onAutorizado(),
    // So' o 409 (termo desatualizado, pendencia que mudou) pede o termo de novo;
    // senha ou codigo errado nao mexem no que esta' na tela.
    onError: (e) => {
      if ((e as { status?: number }).status === 409) void termo.refetch();
    },
  });
  const valido = Boolean(termo.data) && aceite && (canal === "web" ? senha.length > 0 : /^\d{6}$/.test(codigo));
  return (
    <Modal
      open
      title={a.tipo === "inclusao" ? "Autorizar o desconto" : "Autorizar o novo valor"}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={() => autorizar.mutate()} disabled={!valido || autorizar.isPending}>
            {autorizar.isPending ? "Autorizando…" : "Autorizar"}
          </Button>
        </>
      }
    >
      <div style={{ display: "grid", gap: 12 }}>
        {termo.isLoading ? (
          <p style={{ margin: 0 }}>Carregando o termo…</p>
        ) : termo.data ? (
          <p style={{ margin: 0 }} data-testid="termo-autorizacao">{termo.data.texto}</p>
        ) : (
          <p role="alert">{mensagemDeErro(termo.error, "Não foi possível carregar o termo.")}</p>
        )}
        <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 14 }}>
          <input type="checkbox" checked={aceite} onChange={(e) => setAceite(e.target.checked)} />
          <span>Li e autorizo este desconto.</span>
        </label>
        {a.canal === "ambos" ? (
          <div role="radiogroup" aria-label="Como confirmar" style={{ display: "flex", gap: 16, fontSize: 14 }}>
            <label><input type="radio" checked={canal === "web"} onChange={() => setCanal("web")} /> Confirmar aqui</label>
            <label><input type="radio" checked={canal === "token"} onChange={() => setCanal("token")} /> Código por e-mail</label>
          </div>
        ) : null}
        {canal === "web" ? (
          <TextField
            label="Sua senha"
            type="password"
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
            hint="A de transação; sem ela, a sua senha de acesso."
          />
        ) : (
          <div style={{ display: "grid", gap: 8 }}>
            <Button variant="ghost" size="sm" onClick={() => pedirCodigo.mutate()} disabled={pedirCodigo.isPending}>
              {pedirCodigo.isPending ? "Enviando…" : envio ? "Enviar outro código" : "Enviar código por e-mail"}
            </Button>
            {envio ? <p role="status" style={{ margin: 0, fontSize: 13 }}>{envio}</p> : null}
            <TextField
              label="Código de 6 dígitos"
              value={codigo}
              inputMode="numeric"
              onChange={(e) => setCodigo(e.target.value.replace(/\D/g, "").slice(0, 6))}
            />
          </div>
        )}
        {pedirCodigo.isError && <p role="alert">{mensagemDeErro(pedirCodigo.error, "Não foi possível enviar o código.")}</p>}
        {autorizar.isError && <p role="alert">{mensagemDeErro(autorizar.error, "Não foi possível autorizar.")}</p>}
      </div>
    </Modal>
  );
}
